
<template>
  <Card class="insights-card resources-card">
    <template #title>
      <div class="card-title resources-title">
        <i class="pi pi-link card-icon" />
        Further Reading
      </div>
    </template>
    <template #content>
      <div
        v-if="resources.length"
        class="resources-list"
      >
        <div
          v-for="(resource, index) in resources"
          :key="index"
          class="resource-item"
        >
          <div class="resource-info">
            <Tag
              :value="resource.type === INSIGHTS_TYPE_RESOURCE ? 'Lib' : 'Web'"
              :severity="resource.type === INSIGHTS_TYPE_RESOURCE ? 'success' : 'info'"
              class="source-tag"
            />
            <span class="resource-title">{{ resource.title }}</span>
          </div>
          <div class="resource-actions">
            <Button
              v-if="resource.type === INSIGHTS_SOURCE_WEB"
              icon="pi pi-external-link"
              text
              :label="isShowLabel ? 'Open' : ''"
              class="open-btn"
              @click="openWebResource(resource.title)"
            />
            <Button
              v-if="resource.type === INSIGHTS_SOURCE_WEB"
              :icon="savedResources[index] ? 'pi pi-check' : 'pi pi-bookmark'"
              :label="isShowLabel ? (savedResources[index] ? 'Saved' : 'Save') : ''"
              :loading="savingResources[index]"
              :disabled="savedResources[index]"
              text
              class="open-btn"
              @click="saveWebResource(resource, index)"
            />
            <Button
              v-if="resource.type === INSIGHTS_TYPE_RESOURCE && isWebUrl(resource.title)"
              icon="pi pi-external-link"
              text
              :label="isShowLabel ? 'Open' : ''"
              class="open-btn"
              @click="openWebResource(resource.title)"
            />
            <Button
              v-if="resource.type === INSIGHTS_TYPE_RESOURCE"
              icon="pi pi-book"
              text
              :label="isShowLabel ? 'Read' : ''"
              class="open-btn"
              @click="openResourceReader(resource)"
            />
          </div>
        </div>
      </div>
      <p
        v-else
        class="empty-state"
      >
        No resources found for this session.
      </p>
    </template>
  </Card>
</template>

<script>
import Card from 'primevue/card'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { INSIGHTS_SOURCE_WEB, INSIGHTS_SOURCE_LIBRARY, INSIGHTS_TYPE_RESOURCE } from '@/assets/constants/constants'
import { StorageService } from '@/services/StorageService'
import { inferResourceType, saveUrlResource } from '@/services/knowledgebaseService'

export default {
  name: 'InsightsResources',
  components: { Card, Button, Tag },
  props: {
    resources: {
      type: Array,
      required: true
    },
    subject: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      INSIGHTS_SOURCE_WEB,
      INSIGHTS_SOURCE_LIBRARY,
      INSIGHTS_TYPE_RESOURCE,
      savingResources: {},
      savedResources: {},
      isShowLabel: window.innerWidth >= 768
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.isShowLabel = window.innerWidth >= 768
    },
    openWebResource(url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    async saveWebResource(resource, index) {
      const subjectId = this.subject
      if (!subjectId) {
        this.$toast.add({ severity: 'warn', summary: 'Cannot Save', detail: 'No active session subject found.', life: 3000 })
        return
      }
      this.savingResources = { ...this.savingResources, [index]: true }
      try {
        await saveUrlResource(resource.path, subjectId)
        this.savedResources = { ...this.savedResources, [index]: true }
        this.$toast.add({ severity: 'success', summary: 'Saved', detail: 'Resource saved to your library.', life: 3000 })
      } catch (error) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save resource. Please try again.', life: 3000 })
      } finally {
        this.savingResources = { ...this.savingResources, [index]: false }
      }
    },
    isWebUrl(path) {
      console.log('Checking if path is a web URL:', path)
      return typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))
    },
    openResourceReader(resource) {
      const resourceContext = {
        id: resource.id,
        resource_name: resource.title,
        resource_type: inferResourceType(resource.title),
        is_sample: false
      }
      StorageService.saveResourceContext(resourceContext)
      const route = this.$router.resolve({ name: 'ResourceReader' })
      window.open(route.href, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
.insights-card {
  width: 100%;
  border-radius: 14px;
  background: #f0fdfa;
  border: 1.5px solid #14b8a6;
  box-shadow: 0 2px 10px rgba(20, 184, 166, 0.08);
  margin-bottom: 0;
  padding: 0;
}

.resources-card {
  background: linear-gradient(120deg, #f0fdfa 60%, #ccfbf1 100%);
  border-color: #14b8a6;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.08rem;
  font-weight: 700;
  color: #0f766e;
  padding: 10px 0 0 0;
}

.resources-title {
  color: #0f766e;
}

.card-icon {
  color: #14b8a6;
  font-size: 1.2rem;
}

.resources-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.resource-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #99f6e4;
  border-radius: 8px;
  background: #ecfeff;
}

.resource-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.source-tag {
  flex-shrink: 0;
}

.resource-title {
  font-size: 0.97rem;
  color: #134e4a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 767px) {
  .resource-title {
    font-size: 0.85rem;
  }

  .resource-item {
    padding: 8px 10px;
    gap: 8px;
  }

  .source-tag {
    font-size: 0.75rem;
  }
}

.open-btn {
  flex-shrink: 0;
}

.resource-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.empty-state {
  margin: 0;
  color: #9ca3af;
  font-style: italic;
  font-size: 0.95rem;
}
</style>
